import { useEffect, useRef, useState } from "react";
import type { Session } from "@cs2/contracts/transport";
import type { NavigateFunction } from "react-router-dom";
import { getSession, refreshSession } from "../api/client.js";
import { useNotification } from "../ui/index.js";
import { statusOf } from "../api/ApiError.js";

/** Der Access-Cookie hält eine Stunde; ein Puffer von 15 Minuten reicht. */
const sessionRefreshIntervalMs = 45 * 60 * 1000;

export interface UseSessionOptions {
  /** Wohin nach dem Login zurückgesprungen wird. */
  redirectTo: string;
  /** Einzelne geschützte Features dürfen auch im Dev-Modus nie anonym bleiben. */
  requireAuthentication?: boolean;
}

/**
 * Sitzung laden, halten und erneuern — für Editor und Quickview.
 *
 * Bis Phase 8 gab es das zweimal: `editor/hooks/useEditorBootstrap.ts` und
 * `quickview/useQuickviewSession.ts` waren bis auf den Payload-Fetch wortgleich,
 * inklusive derselben Konstante und desselben Refresh-Effekts. Zwei Kopien
 * einer sicherheitsrelevanten Logik driften garantiert; hier gibt es sie
 * einmal.
 *
 * Der Refresh-Timer ist kein Komfort: ohne ihn endet jede Sitzung nach einer
 * Stunde mit einem stillen 401 — die Quickview kann während eines Matches
 * stundenlang offen stehen.
 */
export function useSession(
  navigate: NavigateFunction,
  options: UseSessionOptions,
) {
  const { notify } = useNotification();
  const [session, setSession] = useState<Session | null>(null);
  const [checking, setChecking] = useState(true);
  const sessionRef = useRef<Session | null>(null);

  function adoptSession(next: Session | null) {
    sessionRef.current = next;
    setSession(next);
  }

  const { redirectTo, requireAuthentication = false } = options;

  useEffect(() => {
    let active = true;
    function toLogin() {
      const target =
        redirectTo === "/editor"
          ? "/editor/login"
          : `/editor/login?redirectTo=${encodeURIComponent(redirectTo)}`;
      void navigate(target, { replace: true });
    }
    getSession()
      .then((next) => {
        if (!active) return;
        adoptSession(next);
        // Im Entwicklungsmodus bleibt die Seite ohne Anmeldung bedienbar,
        // damit die Oberfläche ohne laufenden Auth-Stack testbar ist.
        if (
          !next.authenticated &&
          (requireAuthentication || !import.meta.env.DEV)
        ) {
          toLogin();
          return;
        }
        setChecking(false);
      })
      .catch((error: unknown) => {
        if (!active) return;
        if (
          statusOf(error) === 401 &&
          (requireAuthentication || !import.meta.env.DEV)
        ) {
          toLogin();
          return;
        }
        setChecking(false);
      });
    return () => {
      active = false;
    };
  }, [navigate, redirectTo, requireAuthentication]);

  useEffect(() => {
    if (!session?.authenticated) return;
    const timer = setInterval(() => {
      const current = sessionRef.current;
      if (!current?.csrfToken) return;
      refreshSession(current.csrfToken)
        .then(adoptSession)
        .catch(() =>
          notify({
            message: "SITZUNG LÄUFT AB – BITTE NEU ANMELDEN",
            kind: "error",
            duration: 0,
          }),
        );
    }, sessionRefreshIntervalMs);
    return () => clearInterval(timer);
  }, [session?.authenticated, notify]);

  return { session, sessionRef, adoptSession, checking };
}
